// src/pages/SuperAdminAccept.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

const s = {
  page: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 16,
    padding: '40px 48px',
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    textAlign: 'center',
  },
  icon: { fontSize: 40, marginBottom: 16 },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--ka-navy)', marginBottom: 8 },
  subtitle: { fontSize: 14, color: '#6b7280', marginBottom: 28, lineHeight: 1.6 },
  inputWrap: { marginBottom: 16 },
  input: {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontSize: 20,
    fontFamily: 'monospace',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textAlign: 'center',
    textTransform: 'uppercase',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s',
  },
  btn: {
    width: '100%',
    padding: '12px',
    background: 'var(--ka-navy)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  error: {
    marginTop: 12,
    padding: '10px 14px',
    background: '#fee2e2',
    border: '1px solid #fca5a5',
    borderRadius: 8,
    fontSize: 13,
    color: '#dc2626',
  },
  success: {
    marginTop: 12,
    padding: '10px 14px',
    background: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: 8,
    fontSize: 13,
    color: '#16a34a',
  },
  note: {
    marginTop: 20,
    fontSize: 12,
    color: '#9ca3af',
    lineHeight: 1.5,
  },
};

export default function SuperAdminAccept() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');

  const handleSubmit = async () => {
    const trimmed = code.trim().toUpperCase();
    if (trimmed.length < 6) {
      setStatus('error');
      setMessage('Please enter the full invite code.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      // Promotion is handled server-side by a callable Cloud Function using the
      // Admin SDK. This is required: a not-yet-superadmin cannot (and must not
      // be able to) write role:'superadmin' to their own user doc from the
      // client — Firestore rules correctly block that. The function verifies
      // targetUid and expiry, promotes, and deletes the single-use invite.
      const functions = getFunctions(undefined, 'us-central1');
      const acceptInvite = httpsCallable(functions, 'acceptSuperAdminInvite');
      await acceptInvite({ code: trimmed });

      // Refresh profile in context so isSuperAdmin updates immediately —
      // without this the user would need a hard reload to see their new access
      await refreshProfile();

      setStatus('success');
      setMessage('You have been promoted to Super Admin. Redirecting…');

      setTimeout(() => navigate('/superadmin'), 1500);

    } catch (err) {
      console.error('SuperAdmin accept error:', err);
      setStatus('error');
      // Callable HttpsError messages surface on err.message — show the
      // server's reason (bad code, wrong account, expired) when present.
      setMessage(err?.message || 'Something went wrong. Please try again.');
    }
  };

  if (profile?.role === 'superadmin') {
    return (
      <div style={s.page}>
        <div style={s.card}>
          <div style={s.icon}>⚡</div>
          <div style={s.title}>You're already a Super Admin</div>
          <button style={s.btn} onClick={() => navigate('/superadmin')}>
            Go to Super Admin Panel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.icon}>🔐</div>
        <div style={s.title}>Accept Super Admin Invite</div>
        <div style={s.subtitle}>
          Enter the invite code you received. Codes are single-use and expire after 24 hours.
        </div>

        <div style={s.inputWrap}>
          <input
            style={s.input}
            type="text"
            placeholder="XXXXXXXX"
            value={code}
            maxLength={10}
            onChange={e => setCode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            disabled={status === 'loading' || status === 'success'}
          />
        </div>

        <button
          style={{ ...s.btn, opacity: status === 'loading' ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={status === 'loading' || status === 'success'}
        >
          {status === 'loading' ? 'Verifying…' : 'Claim Invite'}
        </button>

        {status === 'error' && <div style={s.error}>{message}</div>}
        {status === 'success' && <div style={s.success}>✓ {message}</div>}

        <div style={s.note}>
          You must be signed in with the account the invite was issued to.
          Signed in as: <strong>{user?.email}</strong>
        </div>
      </div>
    </div>
  );
}